/**
 * Exponential reconnect backoff with jitter.
 *
 * Extracted from the activity stream's WebSocket reconnect, which is where the
 * shape was settled: double the delay per attempt, cap it, and spread the
 * result so a fleet of tabs that all lost the same backend does not come back
 * in lockstep and re-flatten it.
 *
 * The boot-failure screen retries on the same schedule. Keeping one formula is
 * what stops "how long until we try again" from meaning two different things in
 * two places.
 */

/** First retry lands ~1s after the failure. */
export const BASE_BACKOFF_DELAY_MS = 1_000;

/** Ceiling, so a long outage settles into one attempt every 30s. */
export const MAX_BACKOFF_DELAY_MS = 30_000;

/** Fraction of the delay added as jitter (0.25 = up to +25%). */
const JITTER_RATIO = 0.25;

/**
 * Delay before retry number `attempt` (0-based: attempt 0 is the first retry).
 *
 * `random` is injectable so the jitter can be pinned in tests; production
 * callers omit it. A negative or non-finite attempt is clamped to 0 rather than
 * producing a fractional or NaN delay that a `setTimeout` would silently treat
 * as zero and hot-loop on.
 */
export function nextBackoffDelayMs(
	attempt: number,
	random: () => number = Math.random,
): number {
	const safeAttempt = Number.isFinite(attempt)
		? Math.max(0, Math.floor(attempt))
		: 0;
	const delay = Math.min(
		BASE_BACKOFF_DELAY_MS * 2 ** safeAttempt,
		MAX_BACKOFF_DELAY_MS,
	);
	return delay + delay * random() * JITTER_RATIO;
}
